import type { FormId } from '@risksmart-app/shared/forms/formConfigRegistry';
import type { FormFieldConfig } from '@risksmart-app/shared/forms/types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  FieldTypeOption,
  FormEditorInitialData,
} from 'src/blocks/form-editor-dialog';

import { useIsFeatureFlagEnabledLazy } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsModuleEnabledLazy } from '@/hooks/useIsModuleEnabled';
import { useFormConfigRegistry } from '@/utils/table/hooks/form/useFormConfigRegistry';
import { getCustomAttributeRenderProps } from '@/utils/table/utils/customAttributes';

import { fieldTypesConfig } from '../custom-attributes/field-types';
import {
  convertConditionsToEditorRules,
  defaultFieldConditionToConditions,
} from './convertEditorStateToFormData';
import { useRiskSmartForm } from './customisable-form/RiskSmartFormContext';
import { useCustomisableFormDataContext } from './customisable-form-data/CustomisableFormDataContext';
import { useFormFieldElementsStore } from './stores/useFormFieldElementsStore';
import { useFormFieldRegistryStore } from './stores/useFormFieldRegistryStore';

const RISK_RATING_FORM_IDS: FormId[] = [
  'controlled_risk_assessment_result',
  'uncontrolled_risk_assessment_result',
  'risk_controlled_internal_audit_result',
  'risk_uncontrolled_internal_audit_result',
  'risk_controlled_oversight_result',
  'risk_uncontrolled_oversight_result',
];

const GROUP_DISPLAY_NAMES: Record<string, string> = {
  testConfigFields: 'Test configuration',
  typeAndDetails: 'Indicator configuration',
};

const getGroupDisplayName = (groupFieldId: string): string =>
  GROUP_DISPLAY_NAMES[groupFieldId] ??
  groupFieldId.replace(/([A-Z])/g, ' $1').trim();

const getGroupSubtitle = (groupFieldId: string): string =>
  groupFieldId === 'testConfigFields' ? 'Test schedule configuration' : '';

/**
 * Maps a custom attribute type to the form editor's field type.
 * Since the editor now uses CustomAttributeFieldType values directly,
 * this is a pass-through for all known types.
 */
const mapCustomAttributeType = (type: string): string => type;

const mapDisplayTypeToFieldType = (
  registryField: FormFieldConfig | undefined
): string => {
  if (!registryField) {
    return '';
  }
  if (!registryField.displayType) {
    return 'text';
  }

  const { displayType } = registryField.displayType;

  switch (displayType) {
    case 'commonLookup':
    case 'rating':
      return 'dropdown';
    case 'users':
      return 'multiselect' in registryField.displayType &&
        registryField.displayType.multiselect === false
        ? 'dropdown'
        : 'usermultiselect';
    case 'departments':
      return 'departmentmultiselect';
    case 'tags':
    case 'badgeList':
      return 'multiselect' in registryField.displayType &&
        registryField.displayType.multiselect === false
        ? 'dropdown'
        : 'multiselect';
    case 'textarea':
      return 'textarea';
    case 'date':
      return 'date';
    case 'number':
      return 'number';
    default:
      return '';
  }
};

interface UseFormEditorInitialDataResult {
  initialData: FormEditorInitialData;
  fieldTypeOptions: FieldTypeOption[];
}

/**
 * Builds the initialData and fieldTypeOptions needed by the FormEditorDialog.
 *
 * Extracts the data transformation logic that was previously inline in FormMenu,
 * reading from the form data context, field registry, and form config registry.
 */
export const useFormEditorInitialData = (
  parentType: FormId
): UseFormEditorInitialDataResult => {
  const { forcedRequiredFields, defaultRequiredFields } = useRiskSmartForm();
  const {
    formSections,
    formFieldConfigurations,
    formFieldOrdering,
    customAttributeSchema,
  } = useCustomisableFormDataContext();
  const registeredFields = useFormFieldRegistryStore(
    (state) => state.getAllFields
  );
  const currentFormElements = useFormFieldElementsStore(
    (state) => state.elements
  );
  const defaultSections = useFormFieldElementsStore(
    (state) => state.defaultSections
  );
  const formConfigRegistry = useFormConfigRegistry();
  const formConfig = formConfigRegistry[parentType];
  const { t } = useTranslation(['common']);
  const isModuleEnabled = useIsModuleEnabledLazy();

  const initialData = useMemo((): FormEditorInitialData => {
    const sortedSections = [...(formSections ?? [])].sort(
      (a, b) => (a.ordering?.Position ?? 0) - (b.ordering?.Position ?? 0)
    );

    // Inverse of the save-time translation: form-value key → editor id.
    // Custom fields go from server format back to scope format; standard
    // fields go from canonical casing back to the React key the form uses.
    // We can't assume the React key is the canonical FieldId lowercased —
    // many forms use camelCase keys (e.g. `policyBreach`) — so prefer the
    // actual registered key (matched case-insensitively on FieldId); fall
    // back to canonical-lowercase only when the field isn't registered.
    const formValueKeyToEditorId = (formValueKey: string): string => {
      if (formValueKey.startsWith('CustomAttributeData.')) {
        return `#/properties/${formValueKey.slice('CustomAttributeData.'.length)}`;
      }
      const lowerKey = formValueKey.toLowerCase();
      const registered = registeredFields().find(
        (r) => r.fieldId.toLowerCase() === lowerKey
      );
      if (registered) {
        return registered.fieldId;
      }
      const standard = formConfig
        ? Object.values(formConfig).find((f) => f.fieldId === formValueKey)
        : undefined;

      return standard ? standard.fieldId.toLowerCase() : formValueKey;
    };
    const remapRules = <T extends { ifField: string }>(rules: T[]): T[] =>
      rules.map((r) => ({ ...r, ifField: formValueKeyToEditorId(r.ifField) }));

    const orderByPosition = (fieldIds: string[]) =>
      [...fieldIds].sort((a, b) => {
        const aPos =
          formFieldOrdering?.find((o) => o.FieldId === a)?.Position ?? 0;
        const bPos =
          formFieldOrdering?.find((o) => o.FieldId === b)?.Position ?? 0;

        return aPos - bPos;
      });

    // Mirror useSectionManager's precedence: customer-defined DB sections win;
    // otherwise fall back to the form's hardcoded `<DefaultSection>` defaults;
    // otherwise a single "Details" section holding every field. Uses the
    // elements store (now scoped per form instance via CustomisableFormDataProvider)
    // rather than the global field registry to avoid including fields from other
    // mounted forms.
    const allFieldIds = currentFormElements.map((e) => e.key);
    const buildDefaultSections = () => {
      if (defaultSections.length === 0) {
        return [
          {
            id: 'section_details',
            name: 'Details',
            fieldIds: orderByPosition(allFieldIds),
          },
        ];
      }

      const builtSections = defaultSections.map((s) => ({
        id: s.SectionId,
        name: s.Label,
        fieldIds: orderByPosition(s.fields),
      }));

      // Park fields not mapped to any default section (e.g. runtime-injected
      // custom attributes) at the end of the last section, matching
      // useSectionManager.
      const mappedFieldIds = new Set(builtSections.flatMap((s) => s.fieldIds));
      const unmappedFieldIds = allFieldIds.filter(
        (fieldId) => !mappedFieldIds.has(fieldId)
      );
      if (unmappedFieldIds.length > 0) {
        builtSections[builtSections.length - 1].fieldIds.push(
          ...unmappedFieldIds
        );
      }

      return builtSections;
    };

    const sections =
      sortedSections.length === 0
        ? buildDefaultSections()
        : sortedSections.map((s) => {
            const sectionConditions = convertConditionsToEditorRules(
              s.Conditions
            );

            return {
              id: s.SectionId,
              name: s.Label,
              fieldIds: orderByPosition((s.fields ?? []).map((f) => f.FieldId)),
              ...(sectionConditions.enabled
                ? {
                    config: {
                      name: s.Label,
                      description: s.Description ?? '',
                      conditionalLogicEnabled: true,
                      conditionalLogicOperation: sectionConditions.operation,
                      conditionalLogicRules: remapRules(
                        sectionConditions.rules
                      ),
                      guidanceEnabled: false,
                      guidance: '',
                    },
                  }
                : {}),
            };
          });

    // Build a lookup from custom attribute schema for custom field labels/types
    const customFieldRenderProps = customAttributeSchema
      ? getCustomAttributeRenderProps(
          customAttributeSchema.Schema,
          customAttributeSchema.UiSchema
        )
      : [];
    const customFieldByScope = new Map(
      customFieldRenderProps.map((rp) => [rp.scope, rp])
    );

    const isRiskRatingForm = RISK_RATING_FORM_IDS.includes(parentType);
    const fields: FormEditorInitialData['fields'] = {};
    // Rows store the form-value key space (server format
    // `CustomAttributeData.{path}` for custom fields, canonical casing for
    // standard fields); the registry uses editor ids (`#/properties/{path}`
    // / React keys), so index rows by both the raw FieldId (exact matches
    // take precedence, preserving the binding when duplicate-format rows
    // exist) and the translated editor id.
    type FieldConfigRow = NonNullable<typeof formFieldConfigurations>[number];
    const configsByFieldId = new Map<string, FieldConfigRow>();
    const configsByEditorId = new Map<string, FieldConfigRow>();
    for (const fc of formFieldConfigurations ?? []) {
      if (!fc.FieldId) {
        continue;
      }
      if (!configsByFieldId.has(fc.FieldId)) {
        configsByFieldId.set(fc.FieldId, fc);
      }
      const editorId = formValueKeyToEditorId(fc.FieldId);
      if (!configsByEditorId.has(editorId)) {
        configsByEditorId.set(editorId, fc);
      }
    }

    for (const reg of registeredFields()) {
      const config =
        configsByFieldId.get(reg.fieldId) ?? configsByEditorId.get(reg.fieldId);
      const registryField = formConfig
        ? Object.values(formConfig).find(
            (f) => f.fieldId.toLowerCase() === reg.fieldId.toLowerCase()
          )
        : undefined;

      const isGroup = !reg.isCustom && !registryField;
      const customRenderProp = reg.isCustom
        ? customFieldByScope.get(reg.fieldId)
        : undefined;

      const fieldIdLower = reg.fieldId.toLowerCase();
      const isForcedRequired = forcedRequiredFields.some(
        (f) => f.toLowerCase() === fieldIdLower
      );
      const isDefaultRequired = defaultRequiredFields.some(
        (f) => f.toLowerCase() === fieldIdLower
      );

      // Resolve display name: custom field label from schema, config override,
      // static registry label, group name, or fallback to fieldId
      const resolvedName = isGroup
        ? getGroupDisplayName(reg.fieldId)
        : (config?.Label ??
          customRenderProp?.label ??
          registryField?.formLabel ??
          reg.fieldId);

      // Default/original label — what the field would render with no override.
      // Used by the field editor as a read-only reference when the user has
      // applied a custom label (i.e. resolvedName differs from this).
      const originalLabel = isGroup
        ? undefined
        : (registryField?.formLabel ?? customRenderProp?.label);

      // Map custom attribute options to the editor's { id, label } format
      const editorOptions =
        customRenderProp?.options?.map((opt) => ({
          id:
            opt._tag === 'AltValueOption'
              ? (opt as { AltValue: string }).AltValue
              : opt.Value,
          label: opt.Value,
        })) ?? [];

      // Seed conditional logic from a code-declared default when no persisted
      // condition exists (mirrors the section-default seeding). A read-only
      // default always wins for display and is locked; an editable default is
      // shown pre-filled and only persists once the admin edits it.
      const defaultCondition = registryField?.defaultConditions;
      // Mirror getEffectiveFieldConditions' definition of "has a persisted
      // override" (non-empty tokenGroups) so editor seeding and runtime never
      // disagree on whether a default applies.
      const persistedConditions =
        config?.Conditions && config.Conditions.tokenGroups.length > 0
          ? config.Conditions
          : null;

      // Merge mode (readOnly + merge): seed the locked default rules ahead of
      // any persisted customer rules, marking only the defaults `locked`. The
      // field-level lock markers are NOT used — per-row `locked` supersedes
      // them, leaving the customer rows fully editable and persistable.
      const isMergeDefault =
        !!defaultCondition?.readOnly && !!defaultCondition?.merge;

      let conditionalLogicRules: {
        ifField: string;
        operator: string;
        values: string[];
        locked?: boolean;
      }[];
      let conditionalLogicEnabled: boolean;
      let conditionalLogicOperation: 'and' | 'or';
      let conditionalLogicLocked: boolean;
      let conditionalLogicIsDefault: boolean;

      if (isMergeDefault) {
        const lockedRules = remapRules(
          convertConditionsToEditorRules(
            defaultFieldConditionToConditions(defaultCondition)
          ).rules
        ).map((r) => ({ ...r, locked: true }));
        const customerConditions = persistedConditions
          ? convertConditionsToEditorRules(persistedConditions)
          : null;
        const customerRules = customerConditions
          ? remapRules(customerConditions.rules)
          : [];
        conditionalLogicRules = [...lockedRules, ...customerRules];
        conditionalLogicEnabled = conditionalLogicRules.length > 0;
        // Locked defaults are AND-only; the combinator belongs to the
        // customer's own rule set.
        conditionalLogicOperation = customerConditions?.operation ?? 'and';
        conditionalLogicLocked = false;
        conditionalLogicIsDefault = false;
      } else {
        const effectiveConditions = defaultCondition?.readOnly
          ? defaultFieldConditionToConditions(defaultCondition)
          : (persistedConditions ??
            (defaultCondition
              ? defaultFieldConditionToConditions(defaultCondition)
              : null));
        const fieldConditions =
          convertConditionsToEditorRules(effectiveConditions);
        conditionalLogicRules = remapRules(fieldConditions.rules);
        conditionalLogicEnabled = fieldConditions.enabled;
        conditionalLogicOperation = fieldConditions.operation;
        conditionalLogicLocked =
          !!defaultCondition?.readOnly && fieldConditions.enabled;
        conditionalLogicIsDefault =
          !defaultCondition?.readOnly &&
          !persistedConditions &&
          !!defaultCondition &&
          fieldConditions.enabled;
      }
      const fieldType = customRenderProp
        ? mapCustomAttributeType(customRenderProp.type)
        : mapDisplayTypeToFieldType(registryField);

      // Surface a persisted default value back into the editor so reopening the
      // dialog shows the saved value with the toggle on (the save path writes
      // form_field_configuration.DefaultValue).
      const hasPersistedDefault =
        config?.DefaultValue != null && config.DefaultValue !== '';

      fields[reg.fieldId] = {
        name: resolvedName,
        type: fieldType,
        required: config?.Required ?? (isForcedRequired || isDefaultRequired),
        readOnly: config?.ReadOnly ?? false,
        // A field that is *merely* hidden or read-only gets no `config` block
        // below (that spread is gated on options / conditional logic / a
        // persisted default), so these top-level flags are the only carrier for
        // its state. Dropping `hidden` here made the editor rehydrate it as
        // false and the next save silently unhide the field (CUS-321).
        hidden: config?.Hidden ?? false,
        isCustom: reg.isCustom,
        forcedRequired: isForcedRequired,
        canDelete: reg.isCustom,
        canEditLabel:
          reg.isCustom ||
          (!isRiskRatingForm && !reg.fieldId.includes('schedule.')),
        canEditOptions: !isForcedRequired,
        subtitle: isGroup ? getGroupSubtitle(reg.fieldId) : undefined,
        isGroup,
        originalLabel,
        ...(editorOptions.length > 0 ||
        conditionalLogicEnabled ||
        hasPersistedDefault
          ? {
              config: {
                fieldType,
                fieldName: resolvedName,
                required:
                  config?.Required ?? (isForcedRequired || isDefaultRequired),
                readOnly: config?.ReadOnly ?? false,
                hidden: config?.Hidden ?? false,
                defaultValueEnabled: hasPersistedDefault,
                defaultValue: config?.DefaultValue ?? '',
                options: editorOptions,
                conditionalLogicEnabled,
                conditionalLogicOperation,
                conditionalLogicRules,
                conditionalLogicLocked,
                conditionalLogicIsDefault,
                guidanceEnabled: false,
                guidance: '',
              },
            }
          : {}),
      };
    }

    // Append custom attribute fields not already mapped to a section,
    // matching useSectionManager behaviour for unmapped custom fields
    if (customAttributeSchema && sections.length > 0) {
      const mappedFieldIds = new Set(sections.flatMap((s) => s.fieldIds));
      const renderProps = getCustomAttributeRenderProps(
        customAttributeSchema.Schema,
        customAttributeSchema.UiSchema
      );
      const unmappedCustomFieldIds: string[] = [];
      for (const rp of renderProps) {
        if (!mappedFieldIds.has(rp.scope) && fields[rp.scope]) {
          unmappedCustomFieldIds.push(rp.scope);
        }
      }
      if (unmappedCustomFieldIds.length > 0) {
        sections[sections.length - 1].fieldIds.push(...unmappedCustomFieldIds);
      }
    }

    return { sections, fields };
  }, [
    formSections,
    formFieldConfigurations,
    formFieldOrdering,
    registeredFields,
    currentFormElements,
    defaultSections,
    formConfig,
    parentType,
    forcedRequiredFields,
    defaultRequiredFields,
    customAttributeSchema,
  ]);

  const isFeatureFlagEnabled = useIsFeatureFlagEnabledLazy();
  const fieldTypeOptions = useMemo(
    (): FieldTypeOption[] =>
      Object.entries(fieldTypesConfig)
        .filter(
          ([, config]) =>
            (!config.hasPermission || config.hasPermission(isModuleEnabled)) &&
            (!config.requiresFeatureFlags ||
              config.requiresFeatureFlags.every((flag) =>
                isFeatureFlagEnabled(flag)
              ))
        )
        .map(([type, config]) => ({
          value: type,
          label: config.i18nKey ? (t(config.i18nKey) as string) : type,
        })),
    [isModuleEnabled, isFeatureFlagEnabled, t]
  );

  return { initialData, fieldTypeOptions };
};
